
import { PropType, computed, defineComponent, onMounted, ref } from "@vue/composition-api";
import {
  ApiUpsertCourseParticipantDto,
  ApiCreateCourseParticipantCommentDto,
  ApiGetCourseParticipantCommentDto,
  ApiGetSectorDto,
  ApiGetHierarchicalMemberOrganizationDto,
  ApiGetCustomerOrganizationAddressListDto,
  ApiGetGuestEmployeewithMemberOrganizationDto,
  ApiGetCourseParticipantStatusesDto,
  ApiInt32IdDto,
} from "@/api/generated/Api";
import { CourseParticipantStatus } from "@/shared/enums/CourseParticipantStatus.enum";
import { ModalType } from "@/shared/enums/modalTypeEnum";
import { ParticipantRoles } from "@/shared/enums/ParticipantRoles.enum";
import { NotificationItemType } from "@/shared/enums/notificationItemEnum";
import { DashboardUser } from "@/shared/constants/dashboardUser";
import { api } from "@/api/api";
import { hasMemberOrgAccess, useRestrictedAccessApi } from "@/shared/helpers/accessLevelApiAdapter";
import { globalLoadingWrapper } from "@/shared/helpers/loadingHelpers";
import { openNotification } from "@/shared/helpers/store.helpers";
import { getValidatableRef } from "@/shared/helpers/typeHelpers";
import { useStore } from "@/shared/useHelpers";
import { StoreState } from "@/store/store.state.interface";

// components
import BaseModalForm from "@/components/shared/modal/BaseModalForm.vue";
import PersonSearchInput from "@/components/common/PersonSearchInput.vue";
import TheMemberOrganizaionModalTreeHierarchy from "@/components/administration/member-organizations/TheMemberOrganizaionModalTreeHierarchy.vue";
import CustomerOrganizationSearchInput from "@/components/common/CustomerOrganizationInput.vue";
import ChatLog from "./ChatLog.vue";
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
  // @ts-ignore
} from "tiptap-vuetify";

import { validateNotEmpty } from "@/shared/helpers/validationHelpers";

export default defineComponent({
  name: "UpsertParticipantModal2",
  emits: ["updateParticipantsTable", "close"],
  components: {
    BaseModalForm,
    PersonSearchInput,
    TheMemberOrganizaionModalTreeHierarchy,
    CustomerOrganizationSearchInput,
    ChatLog,
    TiptapVuetify,
  },
  props: {
    headline: {
      type: String,
      required: true,
    },
    type: {
      type: String as PropType<ModalType>,
      required: true,
    },
    isCourseClosed: {
      type: Boolean,
      required: true,
    },
    isVocationalSchool: {
      type: Boolean,
      required: true,
    },
    enrollmentDeadline: {
      type: String,
      required: false,
      default: "",
    },
    selectedUserId: {
      type: Number,
      required: true,
    },
    chosenMemberOrgId: {
      type: Number,
      required: true,
    },
    chosenSectorId: {
      type: Number,
      required: true,
    },
    courseId: {
      type: Number,
      required: true,
    },
    subOrganizerId: {
      type: Number,
      required: false,
    },
    featureSettings: {
      type: Object,
      required: false,
      default: null,
    },
    initialMemberOrgName: {
      type: String,
      required: false,
      default: "",
    },
    initialStatus: {
      type: String,
      required: false,
      default: "",
    },
    initialCommentText: {
      type: String,
      required: false,
      default: "",
    },
    initialSectorName: {
      type: String,
      required: false,
      default: "",
    },
  },
  setup(props, { emit, refs }) {
    const store = useStore<StoreState>();
    const restrictedAccessApi = useRestrictedAccessApi();
    const applicantData = ref<ApiUpsertCourseParticipantDto>({
      userId: props.selectedUserId || 0,
      status: props.initialStatus || "",
      roleName: ParticipantRoles.Student,
      requiresElectronicInvoice: false,
      memberOrganizationId: props.chosenMemberOrgId || null,
      sectorId: props.chosenSectorId || null,
      purchaserId: undefined,
      invoiceRecipientId: undefined,
    });

    const createdComment = ref<ApiCreateCourseParticipantCommentDto>({
      comment: "",
      isGuestUserVisible: false,
    });

    const courseParticipantStatuses = ref<{ text: string; value: string }[]>([]);
    const selectedMemberOrgName = ref<string>(props.initialMemberOrgName);
    const selectedSectorName = ref<string>(props.initialSectorName);
    const sectors = ref<ApiGetSectorDto[]>([]);
    const hierarchicalMemberOrgs = ref<ApiGetHierarchicalMemberOrganizationDto[]>([]);
    const participantComments = ref<ApiGetCourseParticipantCommentDto[]>([]);
    const organizations = ref<ApiGetCustomerOrganizationAddressListDto[]>([]);

    const canEditName = ref(props.type === ModalType.Add);
    const currentGuestUser = ref<ApiGetGuestEmployeewithMemberOrganizationDto | undefined>(undefined);
    const participantStatusesData = ref<ApiGetCourseParticipantStatusesDto | null>(null);
    const hierarchyLoading = ref(true);
    const statusLoading = ref(true);
    const sectorLoading = ref(true);
    const commentsLoading = ref(false);
    const editingGuestUserVisibility = ref(false);
    const guestUserVisibilityCollection = ref<ApiInt32IdDto[]>([]);
    const isInvoiceAndPurchaserEqual = ref(true);
    const useParticipantAsInvoice = ref(true);
    const currentUserId = ref(Number(window.sessionStorage.getItem(DashboardUser.USER_ID)) || 0);
    const initialParticipantSnapshot = ref<ApiUpsertCourseParticipantDto>({
      ...applicantData.value,
    });

    const onSubmit = async () => {
      const isValid = getValidatableRef(refs.form)?.validate();
      if (!isValid) {
        return;
      }

      // tiptap emits "<p></p>" when empty — strip HTML tags before checking
      const commentText = createdComment.value.comment?.replace(/<[^>]*>/g, "").trim() ?? "";
      const shouldCreateComment = commentText.length > 0;
      const shouldUpdateGuestVisibility = guestUserVisibilityCollection.value.length > 0;

      // Build the upsert payload
      const currentPayload: ApiUpsertCourseParticipantDto = {
        ...applicantData.value,
        purchaserId: useParticipantAsInvoice.value ? null : applicantData.value.purchaserId ?? null,
        invoiceRecipientId: useParticipantAsInvoice.value
          ? null
          : isInvoiceAndPurchaserEqual.value
          ? useParticipantAsInvoice.value
            ? null
            : applicantData.value.purchaserId ?? null
          : applicantData.value.invoiceRecipientId ?? null,
        memberOrganizationId: applicantData.value.memberOrganizationId ?? null,
        sectorId: applicantData.value.sectorId ?? null,
      };
      const initialPayload: ApiUpsertCourseParticipantDto = {
        ...initialParticipantSnapshot.value,
        purchaserId: initialParticipantSnapshot.value.purchaserId ?? null,
        invoiceRecipientId: initialParticipantSnapshot.value.invoiceRecipientId ?? null,
        memberOrganizationId: initialParticipantSnapshot.value.memberOrganizationId ?? null,
        sectorId: initialParticipantSnapshot.value.sectorId ?? null,
      };

      const hasParticipantChanges =
        currentPayload.userId !== initialPayload.userId ||
        currentPayload.status !== initialPayload.status ||
        currentPayload.memberOrganizationId !== initialPayload.memberOrganizationId ||
        currentPayload.sectorId !== initialPayload.sectorId ||
        (currentPayload.purchaserId ?? null) !== (initialPayload.purchaserId ?? null) ||
        (currentPayload.invoiceRecipientId ?? null) !== (initialPayload.invoiceRecipientId ?? null);

      const shouldUpsertParticipant = hasParticipantChanges;

      globalLoadingWrapper({ blocking: true }, async () => {
        try {
          if (shouldCreateComment) {
            if (hasMemberOrgAccess) {
              createdComment.value.isGuestUserVisible = true;
              await api.guestside.createGuestUserCourseParticipantCommentAsync(
                props.courseId,
                applicantData.value.userId,
                createdComment.value
              );
            } else {
              await api.course.createCourseParticipantCommentAsync(
                props.courseId,
                applicantData.value.userId,
                createdComment.value
              );
            }
          }

          if (shouldUpdateGuestVisibility) {
            await api.course.changeCourseParticipantsCommentGuestUserVisibilityAsync(
              props.courseId,
              guestUserVisibilityCollection.value
            );
          }

          if (shouldUpsertParticipant) {
            await restrictedAccessApi.upsertCourseParticipant(props.courseId, currentPayload, currentPayload.userId);
          }

          openNotification(
            store,
            NotificationItemType.Success,
            props.type === ModalType.Edit ? "Deltaker oppdatert" : "Deltaker lagt til"
          );

          emit("updateParticipantsTable");
          emit("close");
        } catch (error: any) {
          console.error("Error saving participant:", error);
          openNotification(store, NotificationItemType.Error, "Feil ved lagring av deltaker");
        }
      });
    };

    const setMemberOrg = (value: ApiGetHierarchicalMemberOrganizationDto) => {
      applicantData.value.memberOrganizationId = value.id || null;
      selectedMemberOrgName.value = value.name || "";
    };

    const updateGuestUserVisibilityCollection = (id: number): void => {
      const commentIds = guestUserVisibilityCollection.value.map((x: ApiInt32IdDto) => x.id);
      if (commentIds.includes(id)) {
        guestUserVisibilityCollection.value.splice(commentIds.indexOf(id), 1);
      } else {
        guestUserVisibilityCollection.value.push({ id });
      }
    };

    const updatePurchaserId = (orgId: number) => {
      applicantData.value.purchaserId = orgId;
    };

    const updateInvoiceRecipientId = (orgId: number) => {
      applicantData.value.invoiceRecipientId = orgId;
    };

    // The effective member org ID for the participant being edited.
    const participantMemberOrgId = computed(
      () => applicantData.value.memberOrganizationId ?? props.chosenMemberOrgId ?? null
    );

    // ---- Hierarchy helpers ----

    const findAncestorPath = (
      nodes: ApiGetHierarchicalMemberOrganizationDto[],
      targetId: number,
      path: number[] = []
    ): number[] | null => {
      for (const node of nodes) {
        const currentPath = [...path, node.id];
        if (node.id === targetId) {
          return currentPath;
        }
        if (node.children?.length) {
          const found = findAncestorPath(node.children, targetId, currentPath);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };

    const collectSubtreeIds = (node: ApiGetHierarchicalMemberOrganizationDto, result: Set<number>): void => {
      result.add(node.id);
      for (const child of node.children ?? []) {
        collectSubtreeIds(child, result);
      }
    };

    const findNode = (
      nodes: ApiGetHierarchicalMemberOrganizationDto[],
      targetId: number
    ): ApiGetHierarchicalMemberOrganizationDto | null => {
      for (const node of nodes) {
        if (node.id === targetId) {
          return node;
        }
        if (node.children?.length) {
          const found = findNode(node.children, targetId);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };

    // Returns true if subOrganizerId is a department (LOCAL_OFFICE = 3 levels deep in the tree).
    const isSubOrganizerDepartment = computed(() => {
      if (!props.subOrganizerId) {
        return false;
      }
      const path = findAncestorPath(hierarchicalMemberOrgs.value, props.subOrganizerId);
      return (path?.length ?? 0) >= 3;
    });

    // Returns true if orgId is a region/main office (not a local department).
    const isRegionOrMainOffice = (orgId: number): boolean => {
      const path = findAncestorPath(hierarchicalMemberOrgs.value, orgId);
      return (path?.length ?? 0) > 0 && (path?.length ?? 0) < 3;
    };

    // True when the course's feature settings allow the PreApproval flow.
    const preApprovalEnabled = computed(
      () =>
        props.featureSettings?.usePreApprovalForCourses === true ||
        props.featureSettings?.bypassEnrollmentDeadlineInGuestPortal === true
    );

    // True if ancestorId is higher in the tree than descendantId.
    const isAncestorOf = (ancestorId: number, descendantId: number): boolean => {
      if (ancestorId === descendantId) {
        return false;
      }
      const path = findAncestorPath(hierarchicalMemberOrgs.value, descendantId);
      return path?.includes(ancestorId) ?? false;
    };

    // True if targetId is lower in the tree than ancestorId.
    const isDescendantOf = (targetId: number, ancestorId: number): boolean => isAncestorOf(ancestorId, targetId);

    // True when featureSettings marks this course as arranged by an accounting department.
    // This overrides normal sub-organizer hierarchy rules.
    const isAccountingDepartment = computed(() => props.featureSettings?.isAccountingDepartment === true);

    // True when the logged-in guest belongs to the root (top-level / main) member organization.
    const isRootGuestUser = computed(() => hasMemberOrgAccess && currentGuestUser.value?.isRootOrganization === true);

    // Returns true if guestOrgId is an ancestor, the same org, or a descendant of participantOrgId.
    const isInSameHierarchy = (
      tree: ApiGetHierarchicalMemberOrganizationDto[],
      participantOrgId: number,
      guestOrgId: number
    ): boolean => {
      if (participantOrgId === guestOrgId) {
        return true;
      }

      const ancestorPath = findAncestorPath(tree, participantOrgId);
      if (ancestorPath?.includes(guestOrgId)) {
        return true;
      }

      const participantNode = findNode(tree, participantOrgId);
      if (!participantNode) {
        return false;
      }

      const descendants = new Set<number>();
      collectSubtreeIds(participantNode, descendants);
      return descendants.has(guestOrgId);
    };

    // Returns true if the current guest user is allowed to edit this participant.
    //
    // SubOrg is DEPARTMENT:
    //   Case 1 – Participant in same/below dept: guests at/above subOrg can edit freely
    //   Case 2 – Participant in parent org (region/main): same/higher guests → free;
    //            subOrg dept guest → only from PreApproved onward
    //   Case 3 – Participant in unrelated org: same-org guest → free;
    //            subOrg/higher guest → only from PreApproved onward
    //
    // SubOrg is REGION or MAIN:
    //   Case 1 – Participant in same org as subOrg: same/higher guests can edit
    //   Case 2 – Participant below subOrg: same-org guest → free;
    //            subOrg/higher → Applied | PreApproved | post-PreApproval (Approved/WL/Denied via preapproval)
    //   Case 3 – Participant in unrelated org: same as case 2
    //
    // WL/Denied on cross-org participant without preApproval enabled → locked (cannot re-edit)
    const canEditParticipant = computed(() => {
      if (!hasMemberOrgAccess) {
        return true;
      }
      if (props.type === ModalType.Add) {
        return true;
      }

      const guestOrgId = currentGuestUser.value?.memberOrganization?.id;
      if (!guestOrgId) {
        return false;
      }

      const participantOrgId = participantMemberOrgId.value;
      if (!participantOrgId) {
        return false;
      }

      const status = props.initialStatus as CourseParticipantStatus | "";
      const subOrgId = props.subOrganizerId;

      // Accounting department overrides all normal subOrg hierarchy rules.
      // Visibility: guest can only see/edit participants in their own org or lower levels
      // (main sees all, region sees region+dept, dept sees only dept).
      // Status editing: root (main) can set final statuses; region/dept can only pre-approve or deny.
      if (isAccountingDepartment.value) {
        const participantInGuestOrgOrBelow =
          participantOrgId === guestOrgId || isDescendantOf(participantOrgId, guestOrgId);
        if (!participantInGuestOrgOrBelow) {
          return false;
        }

        // Root (main): can edit at any stage
        if (isRootGuestUser.value) {
          return true;
        }

        // Region/local: can only pre-approve — so only editable when status is Applied
        return !status || status === CourseParticipantStatus.Applied;
      }

      // No subOrganizer: guest can only edit participants in their own org
      if (!subOrgId) {
        return participantOrgId === guestOrgId;
      }

      const guestIsSubOrg = guestOrgId === subOrgId;
      const guestAboveSubOrg = isAncestorOf(guestOrgId, subOrgId);
      const guestAtOrAboveSubOrg = guestIsSubOrg || guestAboveSubOrg;

      const participantIsSubOrg = participantOrgId === subOrgId;
      const participantAboveSubOrg = isAncestorOf(participantOrgId, subOrgId);
      const participantBelowSubOrg = isDescendantOf(participantOrgId, subOrgId);
      const participantInSubOrgHierarchy = participantIsSubOrg || participantAboveSubOrg || participantBelowSubOrg;
      const participantInGuestHierarchy =
        participantOrgId === guestOrgId || isDescendantOf(participantOrgId, guestOrgId);
      const guestIsRegionOrMain = isRegionOrMainOffice(guestOrgId);

      const guestSameOrg = guestOrgId === participantOrgId;

      // Lock: WL/Denied set directly (no preApproval path) on a cross-org participant is permanent
      if (
        !guestSameOrg &&
        !preApprovalEnabled.value &&
        (status === CourseParticipantStatus.WaitingList || status === CourseParticipantStatus.Denied)
      ) {
        return false;
      }

      // True when status was reached via the PreApproval flow (editable after change)
      const isPostPreApproval =
        preApprovalEnabled.value &&
        [
          CourseParticipantStatus.Approved,
          CourseParticipantStatus.WaitingList,
          CourseParticipantStatus.Denied,
        ].includes(status as CourseParticipantStatus);

      if (isSubOrganizerDepartment.value) {
        // ---- subOrg is a DEPARTMENT ----

        // Case 1: participant in same dept (or below, if dept has children)
        if (participantIsSubOrg || participantBelowSubOrg) {
          return guestAtOrAboveSubOrg;
        }

        // Case 2: participant in parent org (region/main above dept subOrg)
        if (participantAboveSubOrg) {
          // Guest at same level or higher than participant's org → edit freely
          if (guestOrgId === participantOrgId || isAncestorOf(guestOrgId, participantOrgId)) {
            return true;
          }
          // Guest IS the subOrg dept → only from PreApproved or after the PreApproval flow
          if (guestIsSubOrg) {
            return status === CourseParticipantStatus.PreApproved || isPostPreApproval;
          }
          return false;
        }

        // Case 3: participant in unrelated org (not in subOrg hierarchy)
        if (!participantInSubOrgHierarchy) {
          if (guestSameOrg) {
            return true;
          }
          // Backup flow: region/main users can pre-approve/deny Applied participants in own subtree,
          // even when subOrganizer is in a different hierarchy branch.
          if (
            participantInGuestHierarchy &&
            guestIsRegionOrMain &&
            (status === CourseParticipantStatus.Applied || !status)
          ) {
            return true;
          }
          if (guestAtOrAboveSubOrg) {
            return status === CourseParticipantStatus.PreApproved || isPostPreApproval;
          }
          return false;
        }
      } else {
        // ---- subOrg is REGION or MAIN ----

        // Case 1: participant in same org as subOrg (region/main)
        if (participantIsSubOrg) {
          return guestOrgId === subOrgId || isAncestorOf(guestOrgId, subOrgId);
        }

        // Case 2: participant below subOrg (dept/region under region/main subOrg)
        if (participantBelowSubOrg) {
          if (guestSameOrg) {
            return true;
          }
          if (guestAtOrAboveSubOrg) {
            // Applied → WL/Denied directly; PreApproved → Approved/WL/Denied via preapproval
            return (
              status === CourseParticipantStatus.Applied ||
              status === CourseParticipantStatus.PreApproved ||
              isPostPreApproval
            );
          }
          // Guest is in same branch but at a different/lower level → cannot see
          return false;
        }

        // Case 3: participant in unrelated org
        if (!participantInSubOrgHierarchy) {
          if (guestSameOrg) {
            return true;
          }
          // Backup flow: region/main users can pre-approve/deny Applied participants in own subtree,
          // even when subOrganizer is in a different hierarchy branch.
          if (
            participantInGuestHierarchy &&
            guestIsRegionOrMain &&
            (status === CourseParticipantStatus.Applied || !status)
          ) {
            return true;
          }
          if (guestAtOrAboveSubOrg) {
            return (
              status === CourseParticipantStatus.Applied ||
              status === CourseParticipantStatus.PreApproved ||
              isPostPreApproval
            );
          }
          return false;
        }

        // Edge case: participant above subOrg (e.g., main when subOrg is region)
        if (participantAboveSubOrg) {
          return guestOrgId === participantOrgId || isAncestorOf(guestOrgId, participantOrgId);
        }
      }

      return false;
    });

    const isDisabled = computed(() => !canEditParticipant.value);

    const lockedSectorName = computed(() => {
      if (selectedSectorName.value) {
        return selectedSectorName.value;
      }
      const sectorId = applicantData.value.sectorId;
      if (!sectorId) {
        return "";
      }
      return sectors.value.find((s) => s.id === sectorId)?.type ?? "";
    });

    // True if the current user is allowed to add a comment.
    // Admins (no hasMemberOrgAccess) can always comment.
    // Guest users can comment only if their org is in the same hierarchy branch
    // as the participant's member organization (ancestor, same, or descendant).
    const canAddComment = computed(() => {
      if (!hasMemberOrgAccess) {
        return true;
      }
      if (props.type === ModalType.Add) {
        return true;
      }

      const guestOrgId = currentGuestUser.value?.memberOrganization?.id;
      const participantOrgId = participantMemberOrgId.value;

      if (!guestOrgId || !participantOrgId) {
        return false;
      }

      return isInSameHierarchy(hierarchicalMemberOrgs.value, participantOrgId, guestOrgId);
    });

    // Returns a Norwegian tooltip message explaining why the status dropdown / form is disabled.
    const disabledStatusTooltip = computed(() => {
      if (!hasMemberOrgAccess) {
        return "";
      }

      const guestOrgId = currentGuestUser.value?.memberOrganization?.id;
      const participantOrgId = participantMemberOrgId.value;
      const status = props.initialStatus as CourseParticipantStatus | "";
      const subOrgId = props.subOrganizerId;

      // Accounting department scenario
      if (isAccountingDepartment.value) {
        if (!isRootGuestUser.value) {
          if (status === CourseParticipantStatus.PreApproved) {
            return "Deltakeren er forhåndsgodkjent. Endelig godkjenning kan kun utføres av regnskapsavdelingen eller hovedorganisasjonen.";
          }
          if (
            status === CourseParticipantStatus.Approved ||
            status === CourseParticipantStatus.WaitingList ||
            status === CourseParticipantStatus.Denied
          ) {
            return `Deltakeren har status ${status}. Kun hovedorganisasjonen kan endre status videre. Kontakt AOF ved behov.`;
          }
        }
        return `Deltakeren har status ${status}. Kontakt AOF for å endre status.`;
      }

      // Guest can't see/edit this participant at all
      if (guestOrgId && participantOrgId && guestOrgId !== participantOrgId) {
        if (subOrgId) {
          if (
            !preApprovalEnabled.value &&
            (status === CourseParticipantStatus.WaitingList || status === CourseParticipantStatus.Denied)
          ) {
            return `Deltakeren har status ${status} og kan ikke lenger redigeres fra din organisasjon. Kontakt AOF for å endre status.`;
          }
          if (isSubOrganizerDepartment.value) {
            if (
              status !== CourseParticipantStatus.PreApproved &&
              status !== CourseParticipantStatus.Approved &&
              status !== CourseParticipantStatus.WaitingList &&
              status !== CourseParticipantStatus.Denied
            ) {
              return "Deltakeren må forhåndsgodkjennes av sin egen organisasjon før arrangørorganisasjonen kan behandle statusen.";
            }
          } else {
            if (
              status !== CourseParticipantStatus.Applied &&
              status !== CourseParticipantStatus.PreApproved &&
              status !== CourseParticipantStatus.Approved &&
              status !== CourseParticipantStatus.WaitingList &&
              status !== CourseParticipantStatus.Denied
            ) {
              return "Du har ikke tilgang til å redigere denne deltakeren.";
            }
          }
        }
        return "Du kan ikke redigere deltakere fra andre organisasjoner.";
      }

      // Generic fallback when the form is simply disabled
      return `Du kan ikke endre status på deltakeren fordi deltaker har status ${status}. Kontakt AOF for å forandre status.`;
    });

    // Returns a Norwegian tooltip message explaining why the entire form is locked.
    const disabledEditTooltip = computed(() => {
      if (!hasMemberOrgAccess) {
        return "";
      }

      const status = props.initialStatus as CourseParticipantStatus | "";

      if (isAccountingDepartment.value) {
        return "Kun arrangørens regnskapsavdeling og hovedorganisasjonen kan redigere denne deltakeren.";
      }

      if (
        !preApprovalEnabled.value &&
        (status === CourseParticipantStatus.WaitingList || status === CourseParticipantStatus.Denied)
      ) {
        return `Deltakeren har status ${status} og kan ikke lenger redigeres fra din organisasjon. Kontakt AOF for å endre status.`;
      }

      const subOrgId = props.subOrganizerId;
      if (subOrgId && isSubOrganizerDepartment.value) {
        return "Deltakeren tilhører en annen organisasjon og kan bare redigeres etter forhåndsgodkjenning.";
      }

      return "Du har ikke tilgang til å redigere denne deltakeren.";
    });

    const loadParticipantStatuses = async () => {
      try {
        const response = await api.course.getCourseParticipantStatusesAsync();
        participantStatusesData.value = response.data;
      } catch (error) {
        console.error("Error loading participant statuses:", error);
      } finally {
        statusLoading.value = false;
      }
    };

    // Transforms raw status strings from the API into { text, value } objects for v-select.
    const getParticipantStatuses = (): { text: string; value: string }[] => {
      if (!participantStatusesData.value) {
        return [];
      }
      const statuses = hasMemberOrgAccess
        ? participantStatusesData.value.memberOrganizationParticipantStatuses ?? []
        : props.isVocationalSchool
        ? participantStatusesData.value.vocationalSchoolParticipantStatuses ?? []
        : participantStatusesData.value.participantStatuses ?? [];
      return statuses.map((s) => ({ text: s, value: s }));
    };

    // Filter available statuses based on the full hierarchy + sub-organizer rules.
    // PreApproved is only offered when featureSettings enables it.
    //
    // SubOrg is DEPARTMENT:
    //   Case 1 (participant in same/below dept): Applied/Approved/WL/Denied
    //   Case 2 (participant above subOrg): same/higher guest → Applied/PreApproved/WL/Denied;
    //           subOrg dept guest → PreApproved/Approved/WL/Denied
    //   Case 3 (unrelated participant): same-org guest → Applied/PreApproved/WL/Denied;
    //           subOrg/higher guest → PreApproved/Approved/WL/Denied
    //
    // SubOrg is REGION/MAIN:
    //   Case 1 (participant at subOrg): Applied/Approved/WL/Denied
    //   Case 2 (participant below subOrg): same-org guest → Applied/PreApproved/WL/Denied;
    //           subOrg/higher guest → context-sensitive (see code below)
    //   Case 3 (unrelated participant): same as case 2
    const filterStatuses = () => {
      const all = getParticipantStatuses();

      if (!hasMemberOrgAccess) {
        courseParticipantStatuses.value = all;
        return;
      }

      const pick = (allowed: CourseParticipantStatus[]) => {
        const filtered = all.filter((s) => allowed.includes(s.value as CourseParticipantStatus));
        const currentStatus = props.initialStatus || applicantData.value.status;

        // Keep the participant's existing status visible when the guest cannot edit it.
        if (isDisabled.value && currentStatus && !filtered.some((s) => s.value === currentStatus)) {
          filtered.unshift({ text: currentStatus, value: currentStatus });
        }

        courseParticipantStatuses.value = filtered;
      };

      const guestOrgId = currentGuestUser.value?.memberOrganization?.id;
      const participantOrgId = participantMemberOrgId.value;
      const subOrgId = props.subOrganizerId;
      const status = props.initialStatus as CourseParticipantStatus | "";

      // Accounting department: complete override of status filtering.
      // Root (main) → Approved / WaitingList / Denied (final decision authority).
      // Region / local → PreApproved / Denied (mandatory pre-approval step only).
      if (isAccountingDepartment.value) {
        if (isRootGuestUser.value) {
          pick([CourseParticipantStatus.Approved, CourseParticipantStatus.WaitingList, CourseParticipantStatus.Denied]);
        } else {
          pick([CourseParticipantStatus.PreApproved, CourseParticipantStatus.Denied]);
        }
        return;
      }

      if (!subOrgId || !guestOrgId) {
        // No subOrganizer: offer PreApproved/WL/Denied when preApproval enabled, else Approved/WL/Denied
        pick(
          preApprovalEnabled.value
            ? [
                CourseParticipantStatus.Applied,
                CourseParticipantStatus.PreApproved,
                CourseParticipantStatus.WaitingList,
                CourseParticipantStatus.Denied,
              ]
            : [
                CourseParticipantStatus.Applied,
                CourseParticipantStatus.Approved,
                CourseParticipantStatus.WaitingList,
                CourseParticipantStatus.Denied,
              ]
        );
        return;
      }

      const guestIsSubOrg = guestOrgId === subOrgId;
      const guestAboveSubOrg = isAncestorOf(guestOrgId, subOrgId);
      const guestAtOrAboveSubOrg = guestIsSubOrg || guestAboveSubOrg;

      const participantIsSubOrg = participantOrgId === subOrgId;
      const participantAboveSubOrg = participantOrgId ? isAncestorOf(participantOrgId, subOrgId) : false;
      const participantBelowSubOrg = participantOrgId ? isDescendantOf(participantOrgId, subOrgId) : false;
      const participantInSubOrgHierarchy = participantIsSubOrg || participantAboveSubOrg || participantBelowSubOrg;
      const participantInGuestHierarchy = participantOrgId
        ? participantOrgId === guestOrgId || isDescendantOf(participantOrgId, guestOrgId)
        : false;
      const guestIsRegionOrMain = isRegionOrMainOffice(guestOrgId);

      const guestSameOrg = guestOrgId === participantOrgId;

      if (isSubOrganizerDepartment.value) {
        // ---- subOrg is DEPARTMENT ----

        if (participantIsSubOrg || participantBelowSubOrg) {
          // Case 1: participant in same dept → hierarchy guests get full Approved/WL/Denied
          pick([
            CourseParticipantStatus.Applied,
            CourseParticipantStatus.Approved,
            CourseParticipantStatus.WaitingList,
            CourseParticipantStatus.Denied,
          ]);
          return;
        }

        if (participantAboveSubOrg) {
          // Case 2: participant in parent org (region/main)
          if (guestOrgId === participantOrgId || (participantOrgId && isAncestorOf(guestOrgId, participantOrgId))) {
            // Guest at same/higher level → PreApproved/WL/Denied (or direct WL/Denied without preApproval)
            pick(
              preApprovalEnabled.value
                ? [
                    CourseParticipantStatus.Applied,
                    CourseParticipantStatus.PreApproved,
                    CourseParticipantStatus.WaitingList,
                    CourseParticipantStatus.Denied,
                  ]
                : [CourseParticipantStatus.Applied, CourseParticipantStatus.WaitingList, CourseParticipantStatus.Denied]
            );
            return;
          }
          if (guestIsSubOrg) {
            // subOrg dept guest: participant is already PreApproved, can now finalize
            pick([
              CourseParticipantStatus.Approved,
              CourseParticipantStatus.WaitingList,
              CourseParticipantStatus.Denied,
            ]);
            return;
          }
        }

        if (!participantInSubOrgHierarchy) {
          // Case 3: participant in unrelated org
          if (guestSameOrg) {
            pick(
              preApprovalEnabled.value
                ? [
                    CourseParticipantStatus.Applied,
                    CourseParticipantStatus.PreApproved,
                    CourseParticipantStatus.WaitingList,
                    CourseParticipantStatus.Denied,
                  ]
                : [CourseParticipantStatus.Applied, CourseParticipantStatus.WaitingList, CourseParticipantStatus.Denied]
            );
            return;
          }
          // Backup flow: region/main users can process Applied participants in own subtree,
          // even when subOrganizer is in another hierarchy branch.
          if (
            participantInGuestHierarchy &&
            guestIsRegionOrMain &&
            (status === CourseParticipantStatus.Applied || !status)
          ) {
            pick([
              CourseParticipantStatus.Applied,
              CourseParticipantStatus.PreApproved,
              CourseParticipantStatus.Denied,
            ]);
            return;
          }
          if (guestAtOrAboveSubOrg) {
            // subOrg/higher: only from PreApproved → Approved/WL/Denied
            pick([
              CourseParticipantStatus.PreApproved,
              CourseParticipantStatus.Approved,
              CourseParticipantStatus.WaitingList,
              CourseParticipantStatus.Denied,
            ]);
            return;
          }
        }
      } else {
        // ---- subOrg is REGION or MAIN ----

        if (participantIsSubOrg) {
          // Case 1: participant in same org as subOrg → full access
          pick([
            CourseParticipantStatus.Applied,
            CourseParticipantStatus.Approved,
            CourseParticipantStatus.WaitingList,
            CourseParticipantStatus.Denied,
          ]);
          return;
        }

        if (participantBelowSubOrg) {
          // Case 2: participant below subOrg (dept/region)
          if (guestSameOrg) {
            pick(
              preApprovalEnabled.value
                ? [
                    CourseParticipantStatus.Applied,
                    CourseParticipantStatus.PreApproved,
                    CourseParticipantStatus.WaitingList,
                    CourseParticipantStatus.Denied,
                  ]
                : [CourseParticipantStatus.Applied, CourseParticipantStatus.WaitingList, CourseParticipantStatus.Denied]
            );
            return;
          }
          if (guestAtOrAboveSubOrg) {
            // Applied → WL/Denied; if already PreApproved/Approved → Approved/WL/Denied
            if (status === CourseParticipantStatus.PreApproved || status === CourseParticipantStatus.Approved) {
              pick([
                CourseParticipantStatus.PreApproved,
                CourseParticipantStatus.Approved,
                CourseParticipantStatus.WaitingList,
                CourseParticipantStatus.Denied,
              ]);
            } else {
              pick([
                CourseParticipantStatus.Applied,
                CourseParticipantStatus.WaitingList,
                CourseParticipantStatus.Denied,
              ]);
            }
            return;
          }
        }

        if (!participantInSubOrgHierarchy) {
          // Case 3: participant in unrelated org — same rules as case 2
          if (guestSameOrg) {
            pick(
              preApprovalEnabled.value
                ? [
                    CourseParticipantStatus.Applied,
                    CourseParticipantStatus.PreApproved,
                    CourseParticipantStatus.WaitingList,
                    CourseParticipantStatus.Denied,
                  ]
                : [CourseParticipantStatus.Applied, CourseParticipantStatus.WaitingList, CourseParticipantStatus.Denied]
            );
            return;
          }
          // Backup flow: region/main users can process Applied participants in own subtree,
          // even when subOrganizer is in another hierarchy branch.
          if (
            participantInGuestHierarchy &&
            guestIsRegionOrMain &&
            (status === CourseParticipantStatus.Applied || !status)
          ) {
            pick([
              CourseParticipantStatus.Applied,
              CourseParticipantStatus.PreApproved,
              CourseParticipantStatus.Denied,
            ]);
            return;
          }
          if (guestAtOrAboveSubOrg) {
            if (status === CourseParticipantStatus.PreApproved || status === CourseParticipantStatus.Approved) {
              pick([
                CourseParticipantStatus.PreApproved,
                CourseParticipantStatus.Approved,
                CourseParticipantStatus.WaitingList,
                CourseParticipantStatus.Denied,
              ]);
            } else {
              pick([
                CourseParticipantStatus.Applied,
                CourseParticipantStatus.WaitingList,
                CourseParticipantStatus.Denied,
              ]);
            }
            return;
          }
        }

        if (participantAboveSubOrg) {
          // Edge case: participant above subOrg (e.g., main when subOrg is region)
          pick([
            CourseParticipantStatus.Applied,
            CourseParticipantStatus.Approved,
            CourseParticipantStatus.WaitingList,
            CourseParticipantStatus.Denied,
          ]);
          return;
        }
      }

      // Fallback: guest has no access to this participant's statuses
      courseParticipantStatuses.value = [];
    };

    const loadHierarchicalMemberOrganizations = async () => {
      try {
        const response = hasMemberOrgAccess
          ? await api.guestside.getHierarchyOfAllOrganizationsGuestUser()
          : await api.organization.getHierarchyOfAllOrganizations();
        hierarchicalMemberOrgs.value = response.data;
      } catch (error) {
        console.error("Error loading hierarchical member organizations:", error);
      } finally {
        hierarchyLoading.value = false;
      }
    };

    const loadSectors = async () => {
      try {
        const response = await api.sectors.getSectorsAsync();
        sectors.value = response.data;
      } catch (error) {
        console.error("Error loading sectors:", error);
      } finally {
        sectorLoading.value = false;
      }
    };

    const getParticipantComments = async () => {
      if (!props.selectedUserId || props.type === ModalType.Add) {
        return;
      }

      commentsLoading.value = true;
      try {
        if (!hasMemberOrgAccess) {
          participantComments.value = (
            await api.course.getCourseParticipantCommentsAsync(props.courseId, props.selectedUserId)
          ).data.reverse();
        } else {
          participantComments.value = (
            await api.guestside.getGuestUserCourseParticipantCommentsAsync(props.courseId, props.selectedUserId)
          ).data.reverse();
        }
      } catch (error) {
        console.error("Error loading comments:", error);
      } finally {
        commentsLoading.value = false;
      }
    };

    onMounted(async () => {
      if (hasMemberOrgAccess) {
        currentGuestUser.value = (await api.guestside.getGuestUserCurrentGuestEmployeeAsync()).data;
      }

      await loadParticipantStatuses();
      await loadHierarchicalMemberOrganizations();

      courseParticipantStatuses.value = getParticipantStatuses();
      if (hasMemberOrgAccess) {
        filterStatuses();
      }

      loadSectors();

      if (props.type !== ModalType.Add) {
        await getParticipantComments();
      }
    });

    return {
      applicantData,
      createdComment,
      courseParticipantStatuses,
      selectedMemberOrgName,
      selectedSectorName,
      sectors,
      canEditName,
      hierarchyLoading,
      statusLoading,
      sectorLoading,
      onSubmit,
      validateNotEmpty,
      ModalType,
      setMemberOrg,
      hierarchicalMemberOrgs,
      participantComments,
      currentUserId,
      editingGuestUserVisibility,
      updateGuestUserVisibilityCollection,
      hasMemberOrgAccess,
      canAddComment,
      isDisabled,
      disabledStatusTooltip,
      disabledEditTooltip,
      isInvoiceAndPurchaserEqual,
      useParticipantAsInvoice,
      organizations,
      updatePurchaserId,
      updateInvoiceRecipientId,
      commentsLoading,
      lockedSectorName,
      minLength: 3,
    };
  },

  data: () => ({
    extensions: [
      History,
      Blockquote,
      Link,
      Underline,
      Strike,
      Italic,
      ListItem,
      BulletList,
      OrderedList,
      [
        Heading,
        {
          options: {
            levels: [1, 2, 3],
          },
        },
      ],
      Bold,
      Code,
      HorizontalRule,
      Paragraph,
      HardBreak,
    ],
  }),
});
